// src/pages/CostControlPage.jsx
// PTY027 — Cost Control module page

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import Toast from "../components/Toast";

import {
  costGetSummary,
  costGetModelPrices,
  costSaveModelPrice,
  costDeleteModelPrice,
} from "../services/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n, decimals = 4) {
  if (n == null) return "—";
  return "$" + Number(n).toFixed(decimals);
}

function fmtK(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

const PROVIDER_COLORS = {
  OpenAI: "#10a37f",
  Google: "#4285f4",
  Perplexity: "#7c3aed",
  Other: "#6b7280",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryCard({ label, value, sub }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "18px 22px",
        flex: "1 1 180px",
      }}
    >
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ProviderTile({ provider, cost, pct }) {
  const color = PROVIDER_COLORS[provider] || PROVIDER_COLORS.Other;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "16px 20px",
        flex: "1 1 160px",
        borderTop: `3px solid ${color}`,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{provider}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{fmt(cost, 4)}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{pct}% of total</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Model Price row (editable)
// ---------------------------------------------------------------------------

function PriceRow({ row, onSave, onDelete }) {
  const [editing, setEditing] = useState(row.isNew || false);
  const [form, setForm] = useState({
    model_name: row.model_name || "",
    provider: row.provider || "",
    input_price_per_1k: row.input_price_per_1k ?? "",
    output_price_per_1k: row.output_price_per_1k ?? "",
    per_request_surcharge: row.per_request_surcharge ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...form, isNew: row.isNew });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
  };

  const cell = { padding: "8px 10px", fontSize: 13, verticalAlign: "middle" };

  if (editing) {
    return (
      <tr style={{ background: "#fafafa" }}>
        <td style={cell}>
          <input
            style={inputStyle}
            value={form.model_name}
            onChange={(e) => set("model_name", e.target.value)}
            placeholder="e.g. gpt-4.1"
            disabled={!row.isNew}
          />
        </td>
        <td style={cell}>
          <input
            style={inputStyle}
            value={form.provider}
            onChange={(e) => set("provider", e.target.value)}
            placeholder="OpenAI"
          />
        </td>
        <td style={cell}>
          <input
            style={inputStyle}
            type="number"
            step="0.000001"
            value={form.input_price_per_1k}
            onChange={(e) => set("input_price_per_1k", e.target.value)}
          />
        </td>
        <td style={cell}>
          <input
            style={inputStyle}
            type="number"
            step="0.000001"
            value={form.output_price_per_1k}
            onChange={(e) => set("output_price_per_1k", e.target.value)}
          />
        </td>
        <td style={cell}>
          <input
            style={inputStyle}
            type="number"
            step="0.001"
            value={form.per_request_surcharge}
            onChange={(e) => set("per_request_surcharge", e.target.value)}
          />
        </td>
        <td style={{ ...cell, color: "#9ca3af" }}>—</td>
        <td style={{ ...cell, whiteSpace: "nowrap" }}>
          <button
            className="btn-primary"
            style={{ padding: "4px 12px", fontSize: 12, marginRight: 6 }}
            onClick={handleSave}
            disabled={saving || !form.model_name}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            className="btn-secondary"
            style={{ padding: "4px 10px", fontSize: 12 }}
            onClick={() => {
              if (row.isNew) onDelete(row.model_name);
              else setEditing(false);
            }}
          >
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={{ ...cell, fontWeight: 500 }}>{row.model_name}</td>
      <td style={{ ...cell, color: PROVIDER_COLORS[row.provider] || "#374151", fontWeight: 500 }}>
        {row.provider}
      </td>
      <td style={cell}>${Number(row.input_price_per_1k).toFixed(6)}</td>
      <td style={cell}>${Number(row.output_price_per_1k).toFixed(6)}</td>
      <td style={cell}>${Number(row.per_request_surcharge).toFixed(4)}</td>
      <td style={{ ...cell, color: "#9ca3af", fontSize: 12 }}>
        {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"}
      </td>
      <td style={{ ...cell, whiteSpace: "nowrap" }}>
        <button
          className="btn-secondary"
          style={{ padding: "4px 10px", fontSize: 12, marginRight: 6 }}
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        {confirmDelete ? (
          <>
            <button
              className="btn-danger"
              style={{ padding: "4px 10px", fontSize: 12, marginRight: 4 }}
              onClick={() => onDelete(row.model_name)}
            >
              Delete
            </button>
            <button
              className="btn-secondary"
              style={{ padding: "4px 8px", fontSize: 12 }}
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            className="btn-secondary"
            style={{ padding: "4px 10px", fontSize: 12, color: "#ef4444", borderColor: "#fca5a5" }}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CostControlPage({ currentUser }) {
  const navigate = useNavigate();
  const userLabel = currentUser?.full_name || currentUser?.email || "User";

  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => setToast({ msg, type });

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await costGetSummary();
      setSummary(data);
    } catch (e) {
      showToast("Failed to load cost summary", "error");
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadPrices = useCallback(async () => {
    setPricesLoading(true);
    try {
      const data = await costGetModelPrices();
      setPrices(data);
    } catch (e) {
      showToast("Failed to load model prices", "error");
    } finally {
      setPricesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadPrices();
  }, [loadSummary, loadPrices]);

  const handleSavePrice = async (data) => {
    try {
      await costSaveModelPrice(data);
      showToast(`${data.model_name} saved`);
      await loadPrices();
    } catch (e) {
      showToast(e?.message || "Save failed", "error");
      throw e;
    }
  };

  const handleDeletePrice = async (modelName) => {
    try {
      await costDeleteModelPrice(modelName);
      setPrices((p) => p.filter((r) => r.model_name !== modelName));
      showToast(`${modelName} deleted`);
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    }
  };

  const addNewRow = () => {
    setPrices((p) => [
      ...p,
      {
        model_name: "",
        provider: "",
        input_price_per_1k: 0,
        output_price_per_1k: 0,
        per_request_surcharge: 0,
        updated_at: null,
        isNew: true,
      },
    ]);
  };

  const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  };

  return (
    <AppShell
      headerSubtitle={`Cost Control • ${userLabel}`}
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => navigate("/modules")}
        />
      }
    >
      <header className="page-header">
        <div>
          <h1 className="page-title">Cost Control</h1>
          <p className="page-sub">LLM spend overview and model price management.</p>
        </div>
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* 1. Summary cards                                                  */}
      {/* ---------------------------------------------------------------- */}
      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Overview</h2>
        {summaryLoading ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</div>
        ) : summary ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <SummaryCard
              label="All-time total"
              value={fmt(summary.all_time_total, 4)}
            />
            <SummaryCard
              label="This month"
              value={fmt(summary.current_month_total, 4)}
            />
            <SummaryCard
              label="LLM Discovery"
              value={fmt(summary.llm_discovery_total, 4)}
              sub="llm_audit + sra_audit + copilot"
            />
            <SummaryCard
              label="ADS Discovery"
              value={fmt(summary.ads_discovery_total, 4)}
              sub="MAD Pilot sessions"
            />
          </div>
        ) : null}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* 2. By provider                                                    */}
      {/* ---------------------------------------------------------------- */}
      {summary && summary.by_provider.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>By Provider</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {summary.by_provider.map((p) => (
              <ProviderTile key={p.provider} {...p} />
            ))}
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* 3. By purpose                                                     */}
      {/* ---------------------------------------------------------------- */}
      {summary && summary.by_purpose.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>By Purpose</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Purpose</th>
                  <th style={thStyle}>Module</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Total cost</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Calls</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Input tokens</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Output tokens</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Avg / call</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_purpose.map((row) => (
                  <tr key={row.purpose_key} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 500, fontSize: 13 }}>{row.purpose}</td>
                    <td style={{ padding: "8px 10px", fontSize: 12, color: "#6b7280" }}>{row.module}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmt(row.cost, 4)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{row.calls.toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmtK(row.input_tokens)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmtK(row.output_tokens)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13, color: "#6b7280" }}>{fmt(row.avg_cost_per_call, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* 4. By model                                                       */}
      {/* ---------------------------------------------------------------- */}
      {summary && summary.by_model.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>By Model</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>Provider</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Total cost</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Calls</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Input tokens</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Output tokens</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Avg / call</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_model.map((row) => (
                  <tr key={row.model} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 500, fontSize: 13, fontFamily: "monospace" }}>{row.model}</td>
                    <td style={{ padding: "8px 10px", fontSize: 12, color: PROVIDER_COLORS[row.provider] || "#374151", fontWeight: 500 }}>{row.provider}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmt(row.cost, 4)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{row.calls.toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmtK(row.input_tokens)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13 }}>{fmtK(row.output_tokens)}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontSize: 13, color: "#6b7280" }}>{fmt(row.avg_cost_per_call, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* 5. Model Price Management                                         */}
      {/* ---------------------------------------------------------------- */}
      <section style={{ marginTop: 36, marginBottom: 40 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Model Prices</h2>
          <button className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }} onClick={addNewRow}>
            + Add model
          </button>
        </div>

        {pricesLoading ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>Provider</th>
                  <th style={thStyle}>In ($/1K tokens)</th>
                  <th style={thStyle}>Out ($/1K tokens)</th>
                  <th style={thStyle}>Surcharge ($/req)</th>
                  <th style={thStyle}>Updated</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((row) => (
                  <PriceRow
                    key={row.model_name || "__new__"}
                    row={row}
                    onSave={handleSavePrice}
                    onDelete={handleDeletePrice}
                  />
                ))}
                {prices.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "20px 10px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                      No model prices configured.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AppShell>
  );
}
